import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Player {
  name: string;
  id: string;
}

interface Team {
  id: string;
  players: Player[];
  currentSetScore: number;
  sets: number[];
  gamesWon: number;
}

interface PadelMatchState {
  team1: Team;
  team2: Team;
  currentSet: number;
  totalSets: number;
  isMatchComplete: boolean;
  matchDuration: number; // em segundos
  startTime: number | null; // timestamp when match started
  status: 'not_started' | 'in_progress' | 'paused' | 'completed';
  winner: string | null;
  matchHistory: {
    point: string;
    timestamp: number;
  }[];
}

// Load state from localStorage if available
const loadState = (): PadelMatchState | undefined => {
  try {
    const serializedState = localStorage.getItem('padelMatch');
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (err) {
    return undefined;
  }
};

const savedState = loadState();

const initialState: PadelMatchState = savedState || {
  team1: {
    id: 'team1',
    players: [],
    currentSetScore: 0,
    sets: [],
    gamesWon: 0
  },
  team2: {
    id: 'team2',
    players: [],
    currentSetScore: 0,
    sets: [],
    gamesWon: 0
  },
  currentSet: 1,
  totalSets: 3,
  isMatchComplete: false,
  matchDuration: 0,
  startTime: null,
  status: 'not_started',
  winner: null,
  matchHistory: []
};

const padelMatchSlice = createSlice({
  name: 'padelMatch',
  initialState,
  reducers: {
    setPlayers(state, action: PayloadAction<{ teamId: string, players: Player[] }>) {
      const { teamId, players } = action.payload;
      if (teamId === 'team1') {
        state.team1.players = players;
      } else if (teamId === 'team2') {
        state.team2.players = players;
      }
    },

    startMatch(state) {
      state.status = 'in_progress';
      state.startTime = Date.now();
    },

    pauseMatch(state) {
      state.status = 'paused';
    },

    resumeMatch(state) {
      state.status = 'in_progress';
    },

    scorePoint(state, action: PayloadAction<{ teamId: string }>) {
      const { teamId } = action.payload;
      const team = teamId === 'team1' ? state.team1 : state.team2;
      const opposingTeam = teamId === 'team1' ? state.team2 : state.team1;

      team.currentSetScore += 1;

      // Log point in history
      state.matchHistory.push({
        point: `Team ${teamId} scored`,
        timestamp: Date.now()
      });

      // Check if set is won (simplified logic, actual padel scoring is more complex)
      if (team.currentSetScore >= 6 && team.currentSetScore - opposingTeam.currentSetScore >= 2) {
        team.sets.push(team.currentSetScore);
        opposingTeam.sets.push(opposingTeam.currentSetScore);
        team.gamesWon += 1;

        // Reset scores for next set
        state.team1.currentSetScore = 0;
        state.team2.currentSetScore = 0;
        state.currentSet += 1;

        // Check if match is complete
        if (team.gamesWon > state.totalSets / 2) {
          state.isMatchComplete = true;
          state.status = 'completed';
          state.winner = teamId;
          state.matchDuration = state.startTime ? (Date.now() - state.startTime) / 1000 : 0;
        }
      }
    },

    undoLastPoint(state) {
      if (state.matchHistory.length > 0) {
        const lastPoint = state.matchHistory[state.matchHistory.length - 1];
        const teamId = lastPoint.point.includes('team1') ? 'team1' : 'team2';
        const team = teamId === 'team1' ? state.team1 : state.team2;

        team.currentSetScore = Math.max(0, team.currentSetScore - 1);
        state.matchHistory.pop();
      }
    },

    resetMatch: {
      reducer: () => {
        return {
          team1: {
            id: 'team1',
            players: [],
            currentSetScore: 0,
            sets: [],
            gamesWon: 0
          },
          team2: {
            id: 'team2',
            players: [],
            currentSetScore: 0,
            sets: [],
            gamesWon: 0
          },
          currentSet: 1,
          totalSets: 3,
          isMatchComplete: false,
          matchDuration: 0,
          startTime: null,
          status: 'not_started' as const,
          winner: null,
          matchHistory: []
        };
      },
      prepare: () => ({
        payload: {}
      })
    },

    setTotalSets(state, action: PayloadAction<number>) {
      state.totalSets = action.payload;
    }
  }
});

export const {
  setPlayers,
  startMatch,
  pauseMatch,
  resumeMatch,
  scorePoint,
  undoLastPoint,
  resetMatch,
  setTotalSets
} = padelMatchSlice.actions;

export default padelMatchSlice.reducer;
